import axios from "axios";

import {
  getInsuranceImagePresignedUrl,
  INSURANCE_IMAGE_FILE_PRIMARY_BACK_SUB_TYPE,
  INSURANCE_IMAGE_FILE_PRIMARY_FRONT_SUB_TYPE,
  INSURANCE_IMAGE_FILE_SECONDARY_BACK_SUB_TYPE,
  INSURANCE_IMAGE_FILE_SECONDARY_FRONT_SUB_TYPE,
  PATIENT_INSURANCE_IMAGE_FILE_TYPE,
  REFERRAL_INSURANCE_IMAGE_FILE_TYPE,
} from "./fileUtil";

/**
 * Fetches a single insurance image with presigned URL
 * Returns a promise that resolves to { message, content_type, status }
 * If fileKey is missing/invalid, returns { message: null }
 */
const getImage = (entityId, fileKey, fileType, fileSubType) => {
  if (!fileKey || fileKey.trim() === "" || fileKey === "None") {
    return new Promise((resolve) => {
      resolve({ message: null });
    });
  }

  return getInsuranceImagePresignedUrl(entityId, fileType, fileSubType);
};

/**
 * Fetches all 4 insurance images (primary front/back, secondary front/back)
 * Returns promise array from axios.all
 */
const fetchInsuranceImageUrls = (entityId, entity, fileType) => {
  const prIns = entity.primary_insurance;
  const secIns = entity.secondary_insurance;

  const pf = prIns.card_front; // primary front
  const pb = prIns.card_back; // primary back
  const sf = secIns.card_front; // secondary front
  const sb = secIns.card_back; // secondary back

  return axios.all([
    getImage(entityId, pf, fileType, INSURANCE_IMAGE_FILE_PRIMARY_FRONT_SUB_TYPE),
    getImage(entityId, pb, fileType, INSURANCE_IMAGE_FILE_PRIMARY_BACK_SUB_TYPE),
    getImage(entityId, sf, fileType, INSURANCE_IMAGE_FILE_SECONDARY_FRONT_SUB_TYPE),
    getImage(entityId, sb, fileType, INSURANCE_IMAGE_FILE_SECONDARY_BACK_SUB_TYPE),
  ]);
};

/**
 * Transforms axios response array into structured object
 * Returns { imageUrls, imageTypes, imageStatuses }
 */
const transformInsuranceImagesResponse = (pfUrl, pbUrl, sfUrl, sbUrl) => {
  return {
    imageStatuses: {
      pbStatus: pbUrl.status,
      pfStatus: pfUrl.status,
      sbStatus: sbUrl.status,
      sfStatus: sfUrl.status,
    },
    imageTypes: {
      pbType: pbUrl.content_type,
      pfType: pfUrl.content_type,
      sbType: sbUrl.content_type,
      sfType: sfUrl.content_type,
    },
    imageUrls: {
      pbUrl: pbUrl.message,
      pfUrl: pfUrl.message,
      sbUrl: sbUrl.message,
      sfUrl: sfUrl.message,
    },
  };
};

/**
 * Fetches insurance images for a patient and returns structured data
 * @param {Object} patient - Patient object with username and insurance data
 * @param {string} fileType - File type constant (default: PATIENT_INSURANCE_IMAGE_FILE_TYPE)
 * @returns {Promise<Object>} { imageUrls, imageTypes, imageStatuses }
 */
export const fetchPatientInsuranceImages = async (
  patient,
  fileType = PATIENT_INSURANCE_IMAGE_FILE_TYPE,
) => {
  const responses = await fetchInsuranceImageUrls(patient.username, patient, fileType);

  return transformInsuranceImagesResponse(...responses);
};

/**
 * Fetches insurance images for a referral and returns structured data
 * @param {Object} referral - Referral object with referralCode and insurance data
 * @returns {Promise<Object>} { imageUrls, imageTypes, imageStatuses }
 */
export const fetchReferralInsuranceImages = async (referral) => {
  const responses = await fetchInsuranceImageUrls(
    referral.referralCode,
    referral,
    REFERRAL_INSURANCE_IMAGE_FILE_TYPE,
  );

  return transformInsuranceImagesResponse(...responses);
};
